import type { TiledLayer, TiledMapData, TiledObject, TiledProperty, TiledTileset } from "pixi-tiledmap";
import type { Vec2 } from "./geometry";

export interface ParsedTerrainObject {
  readonly id: number;
  readonly name: string;
  readonly type: string;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
  readonly rotation: number;
  readonly visible: boolean;
  readonly properties: Readonly<Record<string, unknown>>;
}

export interface ParsedTerrainGrid {
  readonly cols: number;
  readonly rows: number;
  readonly tileSize: number;
  readonly paletteKeys: string[];
  readonly paletteColors: Readonly<Record<string, readonly [number, number, number]>>;
  readonly paletteWalkable: Readonly<Record<string, boolean>>;
  /** grid[row][col] = palette index (0 = empty). */
  readonly grid: number[][];
  readonly objects: readonly ParsedTerrainObject[];
}

export function parseTerrainGridFromTiledMap(map: TiledMapData, tileset: TiledTileset): ParsedTerrainGrid {
  const layer = findTileLayer(map.layers, "ground");
  if (!layer || !Array.isArray(layer.data)) {
    throw new Error("Terrain map missing finite tile layer named ground");
  }
  const tileSize = map.tilewidth;
  const paletteKeys: string[] = [];
  const paletteColors: Record<string, readonly [number, number, number]> = {};
  const paletteWalkable: Record<string, boolean> = {};
  for (let i = 0; i < tileset.tilecount; i++) {
    const tile = tileset.tiles?.find((entry) => entry.id === i);
    if (!tile) throw new Error(`Terrain tileset missing tile ${i}`);
    const key = tile?.type || tile?.properties?.find((prop) => prop.name === "terrain")?.value;
    if (typeof key !== "string" || key.length === 0) {
      throw new Error(`Terrain tileset missing terrain key for tile ${i}`);
    }
    paletteKeys.push(key);
    paletteColors[key] = readColor(tile.properties, key);
    paletteWalkable[key] = readWalkable(tile.properties);
  }

  const grid: number[][] = [];
  for (let row = 0; row < map.height; row++) {
    const values: number[] = [];
    for (let col = 0; col < map.width; col++) {
      values.push(layer.data[row * map.width + col] ?? 0);
    }
    grid.push(values);
  }

  const objects = parseObjectLayers(map.layers);

  return { cols: map.width, rows: map.height, tileSize, paletteKeys, paletteColors, paletteWalkable, grid, objects };
}

/** Whether `pos` sits on a walkable tile. Missing grid, off-map, or empty tiles default to walkable. */
export function isWalkableAt(grid: ParsedTerrainGrid | undefined, pos: Vec2): boolean {
  if (!grid) return true;
  const row = Math.floor(pos.y / grid.tileSize);
  const col = Math.floor(pos.x / grid.tileSize);
  const idx = grid.grid[row]?.[col] ?? 0;
  if (idx === 0) return true;
  const key = grid.paletteKeys[idx - 1];
  return key ? grid.paletteWalkable[key] !== false : true;
}

function findTileLayer(layers: readonly TiledLayer[], name: string): TiledLayer | undefined {
  for (const layer of layers) {
    if (layer.type === "tilelayer" && layer.name === name) return layer;
    if (layer.type === "group" && layer.layers) {
      const child = findTileLayer(layer.layers, name);
      if (child) return child;
    }
  }
  return undefined;
}

function parseObjectLayers(layers: readonly TiledLayer[]): ParsedTerrainObject[] {
  const result: ParsedTerrainObject[] = [];
  for (const layer of layers) {
    if (layer.type === "objectgroup" && layer.objects) {
      for (const obj of layer.objects) {
        result.push({
          id: obj.id,
          name: obj.name,
          type: obj.type,
          x: obj.x,
          y: obj.y,
          width: obj.width,
          height: obj.height,
          rotation: obj.rotation,
          visible: obj.visible,
          properties: readObjectProperties(obj),
        });
      }
    }
    if (layer.type === "group" && layer.layers) {
      result.push(...parseObjectLayers(layer.layers));
    }
  }
  return result;
}

function readObjectProperties(obj: TiledObject): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (!obj.properties) return result;
  for (const prop of obj.properties) {
    result[prop.name] = prop.value;
  }
  return result;
}

function readColor(properties: readonly TiledProperty[] | undefined, key: string): readonly [number, number, number] {
  const raw = properties?.find((prop) => prop.name === "minimapColor")?.value;
  if (typeof raw !== "string") throw new Error(`Terrain tile "${key}" missing minimapColor`);
  const match = /^#?([0-9a-f]{6})$/i.exec(raw);
  if (!match) throw new Error(`Terrain tile "${key}" has invalid minimapColor`);
  const hex = match[1]!;
  return [Number.parseInt(hex.slice(0, 2), 16), Number.parseInt(hex.slice(2, 4), 16), Number.parseInt(hex.slice(4, 6), 16)];
}

function readWalkable(properties: readonly TiledProperty[] | undefined): boolean {
  const raw = properties?.find((prop) => prop.name === "walkable")?.value;
  return typeof raw === "boolean" ? raw : true;
}
